import { useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { SelectWithLabel, TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import { Button } from "newui/components/Button";
import type {
	NotificationRuleType,
	RfxNotificationRuleWritePayload,
} from "../../types/rfx-notification-mgmt.types";

export interface RuleEditorProps {
	typeKey: string;
	targetUserId: number | null;
	inlineError?: string | null;
	isSaving?: boolean;
	onSave: (payload: RfxNotificationRuleWritePayload) => void;
}

const RULE_OPTIONS: Array<{ label: string; value: NotificationRuleType }> = [
	{ label: "Lock on", value: "lock_on" },
	{ label: "Lock off", value: "lock_off" },
	{ label: "Force immediate", value: "force_immediate" },
	{ label: "Force digest", value: "force_digest" },
	{ label: "Deny all", value: "deny_all" },
];

// Rule-editor state + AC2/AC4 wiring: sets lock/force/deny with a required
// reason, surfaces the C17 422 coverage_rule_forbidden error inline on this
// row (never as a silent failure).
export const RuleEditor = ({
	typeKey,
	targetUserId,
	inlineError,
	isSaving,
	onSave,
}: RuleEditorProps) => {
	const [rule, setRule] = useState<NotificationRuleType | null>(null);
	const [reason, setReason] = useState("");

	const selectedOption = RULE_OPTIONS.find((option) => option.value === rule) ?? null;
	const canSave = rule !== null && reason.trim().length > 0;

	const handleSave = () => {
		if (!rule) {
			return;
		}

		onSave({
			type_key: typeKey,
			target_user_id: targetUserId ?? undefined,
			rule,
			reason,
		});
	};

	return (
		<Box data-testid={`rule-editor-${typeKey}`}>
			<SelectWithLabel
				label="Rule"
				testId={`rule-editor-select-${typeKey}`}
				options={RULE_OPTIONS}
				value={selectedOption}
				onChange={(option) =>
					setRule(
						(Array.isArray(option) ? null : option?.value) as NotificationRuleType | null
					)
				}
			/>
			<TextAreaWithLabel
				label="Reason"
				testId={`rule-editor-reason-${typeKey}`}
				value={reason}
				onChange={(event) => setReason(event.target.value)}
			/>
			{inlineError ? (
				<Text
					color="red.500"
					fontSize="sm"
					data-testid={`rule-editor-error-${typeKey}`}
				>
					{inlineError}
				</Text>
			) : null}
			<Button
				data-testid={`rule-editor-save-${typeKey}`}
				disabled={!canSave || isSaving}
				isLoading={isSaving}
				onClick={handleSave}
			>
				Save rule
			</Button>
		</Box>
	);
};
